// ─── PLATFORM UPDATE LOG ──────────────────────────────────────────────────────
// The source of truth for "what changed on the platform", written here rather
// than in the database on purpose: an update note describes a specific build,
// so it should ship in the same commit as the change it announces and be
// reviewable in the same diff. A row typed into a table can drift from what is
// actually deployed, and nobody notices.
//
// The database MIRRORS this list (see the platform_updates sync) — that copy
// exists so a broadcast can record who was notified of which update. It is
// never the place to author one.
//
// TO ADD AN UPDATE
// Append an entry with the next `number` and today's date, in the same commit
// as the feature. Numbers are permanent and customer-facing ("تحديث رقم 3"), so
// never renumber or reuse one; to retract an update, delete the entry and let
// the number retire with it.
//
// `audience` decides who ever sees the entry:
//   'customers' — retailers using the storefront
//   'staff'     — admin/vendor tooling; never shown to customers
//   'all'       — both
//
// `body` lines are rendered as separate bullets, so keep each to one idea.

export const UPDATES = [
  {
    number: 1,
    date: '2026-08-25',
    audience: 'customers',
    title: {
      en: 'Export your order to Excel',
      ar: 'تصدير طلبك إلى Excel',
    },
    body: {
      en: [
        'A "Download Excel" button now sits beside "Download Quote PDF" at the end of your order.',
        'The file carries the SKU, the product name in both English and Arabic, quantity, unit price and line total — with product photos.',
        'Line totals are live formulas: change a quantity and the total recalculates on its own.',
        'Available on any past order too, under "My Orders".',
      ],
      ar: [
        'زر «تحميل Excel» أصبح بجانب «تحميل عرض السعر (PDF)» في نهاية الطلب.',
        'الملف يحتوي على رقم الصنف واسم المنتج بالعربية والإنجليزية والكمية وسعر الوحدة والمجموع، مع صور المنتجات.',
        'خانة المجموع معادلة حية: غيّر الكمية ويتحدّث الإجمالي تلقائيًا.',
        'متاح أيضًا لأي طلب سابق من صفحة «طلباتي».',
      ],
    },
  },
  {
    number: 2,
    date: '2026-09-18',
    audience: 'staff',
    title: {
      en: 'Export the catalogue to Excel — Sedra Home first',
      ar: 'تصدير الكتالوج إلى Excel — وسدرة هوم أولاً',
    },
    body: {
      en: [
        'Products now has an "Export Excel" button beside "Export CSV": the whole catalogue as a formatted .xlsx, however the screen is filtered.',
        'Prices, quantities and stock arrive as real numbers you can sum, the Arabic columns read right-to-left, and every cover image is a clickable link.',
        'A "Supplier" column names the vendor behind each product, so a mixed catalogue reads at a glance.',
        'CSV stays the round-trip format — its columns are still exactly the ones the bulk importer reads.',
        'A "Sedra Home first" button floats first-party products above vendor ones in the list, and both exports follow that order.',
      ],
      ar: [
        'زر «تصدير Excel» أصبح بجانب «تصدير CSV» في صفحة المنتجات: الكتالوج كاملاً في ملف xlsx. منسّق، مهما كانت الفلاتر المفتوحة.',
        'الأسعار والكميات والمخزون تصل كأرقام حقيقية يمكن جمعها، والأعمدة العربية تُقرأ من اليمين لليسار، ورابط صورة كل منتج قابل للنقر.',
        'عمود «المورّد» يوضّح البائع صاحب كل منتج، فيُقرأ الكتالوج المختلط بنظرة واحدة.',
        'ملف CSV يبقى صيغة التعديل وإعادة الاستيراد — أعمدته هي نفسها التي يقرأها الاستيراد المجمع.',
        'زر «سدرة هوم أولاً» يرفع منتجات سدرة هوم فوق منتجات البائعين في القائمة، والتصديران يتبعان نفس الترتيب.',
      ],
    },
  },
];

// Newest first — the order every surface displays them in.
export const updatesFor = (audience = 'customers') =>
  UPDATES
    .filter(u => u.audience === audience || u.audience === 'all')
    .sort((a, b) => b.number - a.number);

// The highest number that exists, i.e. what "fully caught up" means.
export const latestUpdateNumber = (audience = 'customers') =>
  updatesFor(audience).reduce((max, u) => Math.max(max, u.number), 0);
